import React from "react";
import { Button } from "@foundation/altzero_base/base/components/ui/button";
import { Badge } from "@foundation/altzero_base/base/components/ui/badge";
import { Edit, Trash2, Star, Eye, Check, Files, Loader2, PenTool, LayoutList } from "lucide-react";
import { TemplateDocument } from "../../../types/document-library";

function isEditorTemplateContent(content?: string): boolean {
  if (!content) return false;
  try {
    const p = JSON.parse(content);
    return Array.isArray(p.pages) && p.pages.length > 0 && p.pages[0]?.pageSettings !== undefined;
  } catch {
    return false;
  }
}

interface TemplateCardProps {
  template: TemplateDocument;
  onPreview: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onSelect?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  isSelectionMode?: boolean;
  isDeleting?: boolean;
}

export function TemplateCard({
  template,
  onPreview,
  onEdit,
  onDelete,
  onSelect,
  onDuplicate,
  isSelectionMode,
  isDeleting = false,
}: TemplateCardProps) {
  const isDefault =
    (template.rawData?.metadata as any)?.isDefault ||
    (template as any).isDefault;

  const isEditorType = isEditorTemplateContent(template.rawData?.content);

  return (
    <div className="border border-border rounded-lg bg-card hover:shadow-sm transition-shadow flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium leading-snug truncate flex-1" title={template.title}>
            {template.title}
          </p>
          <div className="flex items-center gap-1 flex-shrink-0">
            {isDefault && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                <Star className="w-2.5 h-2.5 mr-0.5" />
                Default
              </Badge>
            )}
            {isEditorType && (
              <Badge className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-600 border-blue-200">
                <PenTool className="w-2.5 h-2.5 mr-0.5" />
                Editor
              </Badge>
            )}
          </div>
        </div>
        {template.description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
            {template.description}
          </p>
        )}
      </div>

      {/* Meta */}
      <div className="px-4 pb-3 flex items-center gap-3 text-xs text-muted-foreground">
        <span>{template.date}</span>
        {template.sections.length > 0 && (
          <span className="flex items-center gap-1">
            <LayoutList className="w-3 h-3" />
            {template.sections.length} section{template.sections.length !== 1 ? 's' : ''}
          </span>
        )}
        {(template as any).category && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {(template as any).category}
          </Badge>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-4 pb-4 pt-0 border-t border-border/50 mt-auto">
        {isSelectionMode ? (
          <div className="flex gap-2 pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPreview(template.id)}
              className="gap-1 text-xs h-7 px-2"
            >
              <Eye className="w-3 h-3" />
              Preview
            </Button>
            <Button
              size="sm"
              onClick={() => onSelect?.(template.id)}
              className="flex-1 gap-1 text-xs h-7"
            >
              <Check className="w-3 h-3" />
              Use Template
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPreview(template.id)}
              className="gap-1 text-xs h-7 px-2"
            >
              <Eye className="w-3 h-3" />
              Preview
            </Button>

            <div className="flex items-center gap-1 ml-auto">
              {onDuplicate && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDuplicate(template.id)}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                  title="Duplicate"
                >
                  <Files className="w-3.5 h-3.5" />
                </Button>
              )}
              {!isDefault && onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(template.id)}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                  title="Edit"
                >
                  <Edit className="w-3.5 h-3.5" />
                </Button>
              )}
              {!isDefault && onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(template.id)}
                  disabled={isDeleting}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600 disabled:opacity-50"
                  title="Delete"
                >
                  {isDeleting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
